import Papa from 'papaparse'

import type { BidPlacementResult } from '@/components/listings/shared/BulkBidModal/useBulkBidMutations'

import type { BadgeTone } from './getPlacedRowDisplay'
import { getPlacedRowDisplay } from './getPlacedRowDisplay'
import { isSupersededRow } from './types'
import type { ValidatedBidSheetRow } from './types'

/**
 * The exact header text for every column this report either echoes from
 * the upload or introduces itself. listingId/minRequiredBid/bidSubmission
 * must match the List View export's own headers (Listings.ListView.header.*)
 * — listingId/bidSubmission because parseBidSheetCsv re-reads a downloaded
 * report by those exact names (the resubmission loop), minRequiredBid for
 * column-identity consistency with the file the buyer originally uploaded.
 */
type AfterUploadReportHeaders = {
  listingId: string
  minRequiredBid: string
  bidSubmission: string
  /**
   * Blanked the same way as `bidSubmission` below — a manifest line's own
   * per-unit price must never survive into the "blank resubmission slot"
   * guarantee. Without this, re-uploading the report as-is would read a
   * stale per-unit value back as a fresh bid (parseBidSheetCsv keeps any row
   * where this is non-blank, even with Bid Submission itself blanked).
   */
  perUnitBid: string
  submittedBid: string
  result: string
  notes: string
  currentWinningBid: string
}

const resultLabel = (tone: BadgeTone): 'winning' | 'losing' | 'error' =>
  tone === 'success' ? 'winning' : tone === 'warning' ? 'losing' : 'error'

/**
 * Builds the After-Upload Report as CSV text (BP-2575). One row per
 * submitted listing (validatedRows already excludes untouched rows — see
 * parseBidSheetCsv's blank-Bid-Submission filter), with the buyer's own
 * uploaded columns echoed verbatim ahead of the report's own columns, and
 * a blank Bid Submission column appended last so the download is itself a
 * valid bid sheet for re-upload. A supersededLot row — a cleared conflicting
 * sibling whose lot already placed through a different row — is left out
 * entirely: the buyer never bid anything on it, so a row with a blank
 * Submitted Bid still labelled Winning/Losing would be misleading, and
 * would inflate this report's row count. The review table is the one place
 * that still shows it, since that's the only place it needs to stay
 * editable.
 */
export const buildAfterUploadReportCsv = (
  allValidatedRows: ValidatedBidSheetRow[],
  placementResultsByListingId: Map<string, BidPlacementResult>,
  headers: AfterUploadReportHeaders,
  formatMessage: (descriptor: { id: string }) => string
): string => {
  const validatedRows = allValidatedRows.filter((row) => !isSupersededRow(row))
  const specialHeaders = new Set([
    headers.listingId,
    headers.minRequiredBid,
    headers.bidSubmission,
    headers.perUnitBid,
    headers.submittedBid,
    headers.result,
    headers.notes,
    headers.currentWinningBid,
  ])

  // Reconstructed from every row rather than just the first, since a single
  // row can be missing a trailing column (see parseBidSheetCsv's "missing
  // trailing cell" case) without that meaning the column doesn't exist.
  const originalHeaders: string[] = []
  const seenOriginalHeaders = new Set<string>()
  for (const row of validatedRows) {
    for (const key of Object.keys(row.rawRow)) {
      if (!specialHeaders.has(key) && !seenOriginalHeaders.has(key)) {
        seenOriginalHeaders.add(key)
        originalHeaders.push(key)
      }
    }
  }

  const fields = [
    ...originalHeaders,
    headers.listingId,
    headers.minRequiredBid,
    headers.submittedBid,
    headers.result,
    headers.notes,
    headers.currentWinningBid,
    headers.bidSubmission,
    headers.perUnitBid,
  ]

  const data = validatedRows.map((row) => {
    const placement = placementResultsByListingId.get(row.listingId)
    const display = getPlacedRowDisplay(row, placement)

    const refreshedMinBid =
      placement?.nextUserMinBidAmount ??
      placement?.nextMinBidAmount ??
      row.listingDetails?.minimumRequiredBid

    const record: Record<string, string> = {
      ...row.rawRow,
      [headers.listingId]: row.listingId,
      [headers.minRequiredBid]:
        refreshedMinBid === undefined ? '' : String(refreshedMinBid),
      [headers.submittedBid]: row.bidSubmissionRaw,
      [headers.result]: resultLabel(display.tone),
      [headers.notes]: display.noteId
        ? formatMessage({ id: display.noteId })
        : (display.noteFallback ?? ''),
      [headers.currentWinningBid]:
        placement?.winningBidAmount === undefined
          ? ''
          : String(placement.winningBidAmount),
      // Always blank — the resubmission slot for a fresh upload. Per-Unit
      // Bid must blank the same way: parseBidSheetCsv keeps a row whose
      // Per-Unit Bid is non-blank even with Bid Submission itself blanked,
      // so leaving the manifest line's old per-unit price in place here
      // would read back as a fresh bid on re-upload.
      [headers.bidSubmission]: '',
      [headers.perUnitBid]: '',
    }

    return fields.map((field) => record[field] ?? '')
  })

  // escapeFormulae guards seller-supplied text (echoed uploaded columns,
  // notes) from running as a spreadsheet formula when the buyer opens this.
  return Papa.unparse({ fields, data }, { escapeFormulae: true })
}
